<script setup lang="ts">
import UIBody from '@/components/ui/UIBody.vue'
import UIPillLinkItem from '@/components/ui/UIPillLinkItem.vue'
import UISpecsList from '@/components/ui/UISpecsList.vue'
import UISpecsListItem from '@/components/ui/UISpecsListItem.vue'
import useFullEffects from '@/composables/data/useFullEffects'
import useLanguage from '@/composables/useLanguage'
import useLayerMetadata from '@/composables/useLayerMetadata'
import { mapRemedyEffectToPillLinkItem } from '@/utils/references'
import { IDataLayer } from '@lifk/types'
import { capitalizeFirstLetter } from '@lifk/utils'
import { computed } from 'vue'

const props = defineProps<{
  /** For which data layer will specs be displayed? */
  dataLayer: IDataLayer
  /** Where will the data layer specs be displayed? */
  position: 'top' | 'bottom' | 'tooltip'
}>()

const i18n = useLanguage('DataLayerSpecs')
const dataLayerRef = computed(() => props.dataLayer)
const { targetAttrUnitStr } = useLayerMetadata(dataLayerRef)
const { effectsByKey } = useFullEffects()

const units = computed(() => {
  if (targetAttrUnitStr.value) {
    return capitalizeFirstLetter(targetAttrUnitStr.value)
  }
  return undefined
})

const relatedEffects = computed(() =>
  props.dataLayer.layerPacks
    .map((layerPack) => {
      const effect = effectsByKey.value[layerPack.effect]
      return effect ? mapRemedyEffectToPillLinkItem(effect) : undefined
    })
    .filter((item) => !!item)
)
</script>

<template>
  <UISpecsList
    :class="{ 'flex-wrap-reverse gap-y-stack-relaxed': position === 'tooltip' }"
  >
    <UISpecsListItem
      v-if="units"
      icon="PhRuler"
      :text="units"
      :class="{ 'hidden sm:flex': position === 'bottom' }"
    />
    <UISpecsListItem
      v-if="dataLayer.lastUpdatedYear"
      icon="PhCalendarBlank"
      :alt="i18n.lastUpdated"
      :text="
        (position === 'tooltip' ? '' : `${i18n.lastUpdated} `) +
        `${dataLayer.lastUpdatedYear}`
      "
      :class="{ 'hidden sm:flex': position === 'bottom' }"
    />
    <UISpecsListItem
      v-if="relatedEffects.length > 0"
      full-width
      :class="{
        'hidden sm:flex': position === 'top',
        '!w-full': position === 'tooltip'
      }"
    >
      <div class="flex gap-y-stack-default flex-wrap">
        <UIBody
          :text="
            position !== 'tooltip'
              ? i18n.relatedEffects
              : relatedEffects.length > 1
                ? i18n.packs
                : i18n.pack
          "
          class="whitespace-nowrap mr-inline-default"
        />
        <UIPillLinkItem
          v-for="effect in relatedEffects"
          :key="effect.key"
          :item="effect"
          :tooltip-disabled="position === 'tooltip'"
          tooltip-direction="bottom"
          tooltip-alignment="center"
          class="[&:not(:last-child)]:mr-inline-relaxed"
        />
      </div>
    </UISpecsListItem>
  </UISpecsList>
</template>
